import { createContext, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { User, Session } from "@supabase/supabase-js";
import { setSentryUser } from "@/lib/sentry";
import { logger } from "@/utils/logger";
import posthog from "posthog-js";
import { hasAnalyticsCookieConsent } from "@/components/CookieConsent";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Track login events - only called once per session
const trackLoginEvent = async (userId: string) => {
  const sessionKey = `login_tracked_${userId}`;
  
  // Check if we already tracked this login in this session
  if (sessionStorage.getItem(sessionKey)) {
    return;
  }

  try {
    await supabase.from("user_engagement_events").insert({
      user_id: userId,
      event_type: "login",
      event_metadata: { timestamp: new Date().toISOString() },
    });
    
    // Mark as tracked for this session
    sessionStorage.setItem(sessionKey, "true");
  } catch (error) {
    logger.debug("Failed to track login event", { error });
  }
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  // Track last known token so we can ignore no-op auth events (tab focus, cross-tab sync)
  const lastTokenRef = useRef<string | null>(null);

  useEffect(() => {
    let tokenRefreshInterval: NodeJS.Timeout | null = null;

    const applySession = (nextSession: Session | null) => {
      const nextToken = nextSession?.access_token ?? null;
      if (nextToken === lastTokenRef.current) {
        // Same token — skip state updates to avoid re-render cascades
        return false;
      }
      lastTokenRef.current = nextToken;
      setSession(nextSession);
      setUser(nextSession?.user ?? null);
      return true;
    };

    // Set up single auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, nextSession) => {
        logger.debug("Auth state changed", { event, hasSession: !!nextSession });

        const changed = applySession(nextSession);
        setLoading(false);

        // Handle token refresh errors
        if (event === "TOKEN_REFRESHED" && !nextSession) {
          logger.warn("Token refresh failed, signing out");
          setSentryUser(null);
          posthog.reset();
          navigateRef.current("/auth");
          return;
        }

        // Only run identification / tracking side-effects when the user actually changed
        if (!changed) return;

        if (nextSession?.user) {
          setSentryUser({
            id: nextSession.user.id,
            email: nextSession.user.email || "unknown",
          });

          if (hasAnalyticsCookieConsent()) {
            posthog.identify(nextSession.user.id, {
              email: nextSession.user.email,
            });
          }

          if (event === "SIGNED_IN") {
            trackLoginEvent(nextSession.user.id);
          }
        } else {
          setSentryUser(null);
          posthog.reset();
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session: existing }, error }) => {
      if (error) {
        logger.warn("Failed to get session", { error: error.message });
        applySession(null);
        setLoading(false);
        return;
      }

      applySession(existing);
      setLoading(false);

      if (existing?.user) {
        setSentryUser({
          id: existing.user.id,
          email: existing.user.email || "unknown",
        });

        // Set up proactive token refresh (every 10 minutes)
        tokenRefreshInterval = setInterval(async () => {
          try {
            const { error } = await supabase.auth.refreshSession();
            if (error) {
              logger.warn("Proactive token refresh failed", { error: error.message });
            }
          } catch (e) {
            logger.debug("Token refresh error", { e });
          }
        }, 10 * 60 * 1000);
      }
    });

    return () => {
      subscription.unsubscribe();
      if (tokenRefreshInterval) {
        clearInterval(tokenRefreshInterval);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      logger.warn("Sign out error", { error });
    }
    
    // Clear only auth-related session storage keys
    const keysToRemove = Array.from({ length: sessionStorage.length }, (_, i) => sessionStorage.key(i))
      .filter((key): key is string => key !== null && key.startsWith('login_tracked_'));
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
    
    // Always clear local state and navigate
    setSentryUser(null);
    posthog.reset();
    setSession(null);
    setUser(null);
    navigate("/");
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
